import React, { useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DollarSign, Plus, X, Download, Upload, Loader2 } from 'lucide-react';
import { PricingCreateForm } from './PricingCreateForm';
import { PricingConfigCard } from './PricingConfigCard';
import PricingInfoCards from './PricingInfoCards';
import { useAdminPricing } from './hooks/useAdminPricing';

const AdminPricingManagement = () => {
  const {
    pricingConfigs, isLoading,
    editingId, editForm, setEditForm,
    showCreateForm, setShowCreateForm,
    managingCountriesId, countriesList, newCountryInput, setNewCountryInput,
    createForm, setCreateForm,
    handleEdit, handleSaveEdit, handleDelete, cancelEdit,
    handleManageCountries, handleCancelManageCountries,
    handleAddCountry, handleRemoveCountry, handleSaveCountries,
    handleCreate,
    handleExport, handleImportFilePick, confirmImport, cancelImport,
    importPreview, importBusy,
  } = useAdminPricing();
  const importInputRef = useRef(null);

  if (isLoading) return <div className="flex justify-center items-center py-12"><div className="text-gray-400">Loading pricing configurations...</div></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white">Pricing Management</h2>
          <p className="text-gray-400 mt-1">Flat monthly per (region, tier). Annual = monthly &times; 10 (2 months free).</p>
          <p className="text-sm text-gray-500 mt-1">Total: {pricingConfigs.length} pricing configurations</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={handleExport} variant="outline"
            className="border-gray-600 text-gray-300 hover:bg-gray-700"
            data-testid="pricing-export-btn"
            title="Download all pricing rows as a JSON backup file">
            <Download className="w-4 h-4 mr-2" />Export
          </Button>
          <Button onClick={() => importInputRef.current?.click()} variant="outline"
            disabled={importBusy}
            className="border-gray-600 text-gray-300 hover:bg-gray-700"
            data-testid="pricing-import-btn"
            title="Restore pricing rows from a previously-exported JSON file">
            {importBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {importBusy ? 'Reading…' : 'Import'}
          </Button>
          <input
            ref={importInputRef} type="file" accept="application/json,.json"
            className="hidden" data-testid="pricing-import-file-input"
            onChange={(e) => {
              const f = e.target.files?.[0];
              handleImportFilePick(f);
              e.target.value = ''; // reset so re-selecting same file re-triggers
            }}
          />
          <Button onClick={() => setShowCreateForm(!showCreateForm)}
            className={showCreateForm ? "bg-gray-500 hover:bg-gray-600 text-white" : "bg-green-500 hover:bg-green-600 text-white"}>
            {showCreateForm ? <><X className="w-4 h-4 mr-2" />Close Form</> : <><Plus className="w-4 h-4 mr-2" />Add Pricing</>}
          </Button>
        </div>
      </div>

      {showCreateForm && <PricingCreateForm createForm={createForm} setCreateForm={setCreateForm} onSubmit={handleCreate} onClose={() => setShowCreateForm(false)} />}

      {pricingConfigs.length > 0 && (
        <Card className="bg-gray-700 border-gray-600">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white"><DollarSign className="w-5 h-5 text-emerald-400" />Pricing by Region &amp; Tier</CardTitle>
            <CardDescription className="text-gray-400">Two rows per region — one for Portfolio Clarity and one for Continuity System.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {pricingConfigs.map((config) => (
                <PricingConfigCard key={config.config_id} config={config} isEditing={editingId === config.config_id}
                  editForm={editForm} setEditForm={setEditForm} onSaveEdit={handleSaveEdit} onCancelEdit={cancelEdit}
                  onEdit={handleEdit} onDelete={handleDelete} isManagingCountries={managingCountriesId === config.config_id}
                  countriesList={countriesList} newCountryInput={newCountryInput} setNewCountryInput={setNewCountryInput}
                  onAddCountry={handleAddCountry} onRemoveCountry={handleRemoveCountry} onSaveCountries={handleSaveCountries}
                  onCancelCountries={handleCancelManageCountries} onManageCountries={handleManageCountries} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {pricingConfigs.length === 0 && <div className="text-center py-12 text-gray-400">No pricing configurations found. Click "Add Pricing" to create one.</div>}
      <PricingInfoCards pricingConfigs={pricingConfigs} />

      {/* Import confirmation modal */}
      <Dialog open={!!importPreview} onOpenChange={(o) => { if (!o) cancelImport(); }}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white" data-testid="pricing-import-confirm-modal">
          <DialogHeader>
            <DialogTitle>Confirm Pricing Import</DialogTitle>
            <DialogDescription className="text-gray-400">
              {importPreview?.fileName && <>From file: <code className="text-gray-300">{importPreview.fileName}</code><br /></>}
              Review the changes that will be applied to this environment.
            </DialogDescription>
          </DialogHeader>
          {importPreview && (
            <div className="space-y-3 my-2">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-green-500/10 border border-green-500/30 rounded p-3" data-testid="import-new-count">
                  <div className="text-2xl font-bold text-green-400">{importPreview.preview.new_count}</div>
                  <div className="text-xs text-gray-400 mt-1">new rows</div>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded p-3" data-testid="import-updated-count">
                  <div className="text-2xl font-bold text-blue-400">{importPreview.preview.updated_count}</div>
                  <div className="text-xs text-gray-400 mt-1">updated rows</div>
                </div>
                <div className="bg-gray-500/10 border border-gray-500/30 rounded p-3" data-testid="import-unchanged-count">
                  <div className="text-2xl font-bold text-gray-400">{importPreview.preview.unchanged_count}</div>
                  <div className="text-xs text-gray-400 mt-1">unchanged</div>
                </div>
              </div>
              <p className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-2">
                <strong>Note:</strong> Rows already in the database but missing from this file will be <strong>left in place</strong> (never deleted).
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={cancelImport} disabled={importBusy}
              className="border-gray-600 text-gray-300 hover:bg-gray-700" data-testid="import-cancel-btn">
              Cancel
            </Button>
            <Button onClick={confirmImport} disabled={importBusy}
              className="bg-green-500 hover:bg-green-600 text-white" data-testid="import-confirm-btn">
              {importBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing…</> : 'Apply Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPricingManagement;
